// Home.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiCopy, FiCheck, FiFileText, FiGithub, FiLinkedin, FiGlobe, 
  FiDatabase, FiShield, FiSmartphone, FiCpu, FiAward, FiBriefcase, FiBookOpen 
} from 'react-icons/fi';
import ProjectCard from './ProjectCard';
import { PROFILE, PROJECTS } from './data';

const SKILLS = {
  backend: {
    title: 'Backend & Cloud',
    icon: <FiDatabase className="text-indigo-400 text-lg" />,
    items: ['Java', 'REST APIs', 'Microservices', 'Node.js', 'Linux VPS', 'PostgreSQL', 'Firestore']
  },
  security: {
    title: 'Security & Networking',
    icon: <FiShield className="text-cyan-400 text-lg" />,
    items: ['Wireshark', 'Nmap', 'APK Reverse Engineering', 'SSL/TLS', 'VirusTotal API', 'Smali & DEX']
  },
  mobile: {
    title: 'Mobile & Frontend',
    icon: <FiSmartphone className="text-emerald-400 text-lg" />,
    items: ['React Native', 'Android Studio', 'React', 'Next.js', 'XML', 'Material UI', 'Tailwind CSS']
  },
  tools: {
    title: 'Dev Tools & Methodologies',
    icon: <FiCpu className="text-purple-400 text-lg" />,
    items: ['Git', 'Docker', 'ONNX Runtime', 'Agile/Scrum', 'Postman', 'ADB Root Shell']
  }
};

const EXPERIENCE = [
  {
    role: 'Full Stack Developer Intern',
    company: 'DreamDart',
    period: '2025',
    icon: <FiBriefcase className="text-indigo-400" />,
    description: 'Designed and implemented end-to-end coding platform features (LeetCode clone) with custom compilers, Firestore data architectures, and optimized Linux VPS reverse proxy deployment via Nginx.',
    tags: ['Full Stack', 'Linux VPS', 'Firestore', 'React']
  },
  {
    role: 'Smart India Hackathon (SIH) National Finalist',
    company: 'IIT Jammu (NCIIPC)',
    period: '2024',
    icon: <FiAward className="text-cyan-400" />,
    description: 'Developed SecureApk, a specialized binary reverse-engineering & permission manipulation system utilizing APKTool, DEX/Smali rebuilding to inject runtime screenshots blocks and lock down permissions.',
    tags: ['Reverse-Engineering', 'Smali', 'Cyber Security', 'Finalist']
  },
  {
    role: 'Department Academic Topper (1st Rank)',
    company: 'Dr. N.G.P. Institute of Technology',
    period: 'CGPA: 9.11',
    icon: <FiBookOpen className="text-yellow-400" />,
    description: 'Maintained the highest academic rank in the Computer Science and Engineering department. Excelled in analysis of algorithms, distributed systems, operating systems, and network cryptography.',
    tags: ['Academics', 'Algorithms', 'Topper']
  },
  {
    role: 'Cybersecurity Hackathon Winner',
    company: 'MSME, Thoothukudi',
    period: 'Winner & Hackathon Selections',
    icon: <FiAward className="text-purple-400" />,
    description: 'Built network scanning tools and defense blueprints targeting local firmware vulnerabilities. Selected as winner for outstanding incident response planning and threat detection implementation.',
    tags: ['Incidence Response', 'Nmap Scanning', 'Ethical Hacking']
  }
];

// Helper function to categorise projects based on tag keywords
const checkProjectDomain = (project, domain) => {
  if (domain === 'All') return true;
  
  const tagsStr = project.tags.join(' ').toLowerCase();
  
  if (domain === 'Security & Cyber') {
    return (
      tagsStr.includes('cyber') || 
      tagsStr.includes('security') || 
      tagsStr.includes('reverse') || 
      tagsStr.includes('apktool') || 
      tagsStr.includes('smali') || 
      tagsStr.includes('threat') || 
      tagsStr.includes('wireshark') || 
      tagsStr.includes('nmap')
    );
  }
  if (domain === 'Backend & Cloud') {
    return (
      tagsStr.includes('java') || 
      tagsStr.includes('node') || 
      tagsStr.includes('vps') || 
      tagsStr.includes('linux') || 
      tagsStr.includes('rest') || 
      tagsStr.includes('firebase') || 
      tagsStr.includes('firestore') || 
      tagsStr.includes('system design')
    );
  }
  if (domain === 'AI/ML') {
    return (
      tagsStr.includes('ai') || 
      tagsStr.includes('ml') || 
      tagsStr.includes('onnx') || 
      tagsStr.includes('forest') || 
      tagsStr.includes('chatbot')
    );
  }
  if (domain === 'Android') {
    return (
      tagsStr.includes('android') || 
      tagsStr.includes('native') || 
      tagsStr.includes('apk') || 
      tagsStr.includes('mobile')
    );
  }
  return false;
};

const Home = () => {
  const [copied, setCopied] = useState(false);
  const [activeFilter, setActiveFilter] = useState('All');

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(PROFILE.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy email: ', err);
    }
  };

  const handleLinkedInClick = () => {
    window.open(PROFILE.linkedin, '_blank');
  };

  const handleGithubClick = () => {
    window.open(PROFILE.github, '_blank');
  };

  // Categorised list of project filters
  const filters = ['All', 'Security & Cyber', 'Backend & Cloud', 'AI/ML', 'Android'];

  // Filter projects by domain & order highlighted projects first
  const filteredProjects = PROJECTS.filter(p => checkProjectDomain(p, activeFilter))
    .sort((a, b) => (b.highlight ? 1 : 0) - (a.highlight ? 1 : 0));

  return (
    <div className="min-h-screen bg-[#0a0d14] text-slate-100 relative overflow-hidden selection:bg-indigo-500/30 selection:text-white">
      {/* Premium Ambient Lights & Grid Background */}
      <div className="absolute inset-0 bg-grid-pattern pointer-events-none opacity-50" />
      <div className="absolute top-0 inset-x-0 h-[600px] bg-radial-hero pointer-events-none" />
      
      {/* Dynamic Glow Circles */}
      <div className="absolute top-[10%] left-[-10%] w-[35vw] h-[35vw] rounded-full bg-indigo-600/10 blur-[120px] animate-pulse-slow" />
      <div className="absolute top-[35%] right-[-10%] w-[30vw] h-[30vw] rounded-full bg-cyan-600/10 blur-[100px] animate-pulse-slow" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10 space-y-24">
        
        {/* HERO SECTION */}
        <section className="flex flex-col items-center text-center space-y-6 pt-8 md:pt-16">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="flex flex-wrap items-center justify-center gap-3"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/30 text-indigo-300 text-xs font-mono font-medium tracking-wide shadow-[0_0_15px_rgba(99,102,241,0.1)]">
              <span className="h-1.5 w-1.5 rounded-full bg-indigo-400 animate-ping" />
              Available for Opportunities
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1, ease: 'easeOut' }}
            className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight max-w-4xl text-white leading-tight"
          >
            Kaarthik Rishaanth — <br className="hidden sm:inline" />
            <span className="text-accent-gradient text-cyber-glow">Computer Science & Business Systems Engineer</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
            className="text-base sm:text-lg md:text-xl text-slate-400 max-w-2xl font-normal leading-relaxed"
          >
            Specializing in <span className="text-slate-200 font-semibold">Java Backend</span>, Distributed Systems, Linux VPS orchestration, <span className="text-slate-200 font-semibold">Application Security</span>, and Mobile Middleware architectures.
          </motion.p>

          {/* Action Bar */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3, ease: 'easeOut' }}
            className="flex flex-wrap items-center justify-center gap-3 pt-4 w-full"
          >
            {/* Copy Email Button */}
            <button
              onClick={handleCopyEmail}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-semibold shadow-[0_4px_20px_rgba(99,102,241,0.35)] hover:shadow-[0_4px_25px_rgba(99,102,241,0.5)] border border-indigo-400/20 active:scale-[0.98] transition-all duration-200 text-sm"
            >
              {copied ? <FiCheck size={16} className="text-emerald-300" /> : <FiCopy size={16} />}
              <span>{copied ? 'Email Copied!' : 'Copy Email'}</span>
            </button>

            {/* View Resume Button */}
            <a
              href="/resume.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-slate-900 border border-white/10 hover:border-slate-700 hover:bg-slate-800 text-slate-200 hover:text-white font-semibold transition-all duration-200 text-sm"
            >
              <FiFileText size={16} />
              <span>View Resume</span>
            </a>

            {/* Icon Quicklinks */}
            <div className="flex items-center gap-1.5 ml-2 border-l border-white/10 pl-4 h-10">
              <button
                onClick={handleGithubClick}
                className="p-2.5 rounded-lg bg-slate-950/60 border border-white/5 text-slate-400 hover:text-white hover:border-white/15 transition-all duration-200"
                title="GitHub Profiles"
              >
                <FiGithub size={18} />
              </button>
              <button
                onClick={handleLinkedInClick}
                className="p-2.5 rounded-lg bg-slate-950/60 border border-white/5 text-slate-400 hover:text-indigo-400 hover:border-indigo-500/30 transition-all duration-200"
                title="LinkedIn Profile"
              >
                <FiLinkedin size={18} />
              </button>
              <a
                href="https://rishaanthlabs.online"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2.5 rounded-lg bg-slate-950/60 border border-white/5 text-slate-400 hover:text-cyan-400 hover:border-cyan-500/30 transition-all duration-200"
                title="Official Website"
              >
                <FiGlobe size={18} />
              </a>
            </div>
          </motion.div>
        </section>

        {/* SKILLS BENTO GRID */}
        <section className="space-y-6">
          <div className="text-center md:text-left space-y-1">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-indigo-400 font-mono">Expertise</h2>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">Technical Skills Bento</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(SKILLS).map(([key, skillGroup], idx) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="p-6 rounded-xl glass-panel glass-panel-hover flex flex-col justify-between space-y-4"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-white/5 border border-white/10">
                    {skillGroup.icon}
                  </div>
                  <h4 className="text-base sm:text-lg font-bold text-white font-mono">
                    {skillGroup.title}
                  </h4>
                </div>

                <div className="flex flex-wrap gap-1.5 pt-2">
                  {skillGroup.items.map((skill, sIdx) => (
                    <span
                      key={sIdx}
                      className="text-xs font-mono px-3 py-1 rounded-full bg-slate-900 border border-slate-800 hover:border-indigo-500/40 hover:bg-slate-950 hover:text-white transition-all duration-300 text-slate-300"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* PROJECTS SECTION */}
        <section className="space-y-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div className="text-center md:text-left space-y-1">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-cyan-400 font-mono">My Works</h2>
              <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">Selected Engineering Projects</h3>
            </div>

            {/* Horizontal Filter Buttons */}
            <div className="flex flex-wrap justify-center gap-1.5 bg-slate-950/80 p-1.5 rounded-xl border border-white/5 overflow-x-auto">
              {filters.map((filter) => (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  className={`px-3 py-1.5 text-xs font-mono font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
                    activeFilter === filter
                      ? 'bg-indigo-600 text-white shadow-[0_0_10px_rgba(99,102,241,0.2)]'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>

          {/* Grid layout containing cards */}
          <motion.div 
            layout
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            <AnimatePresence mode="popLayout">
              {filteredProjects.map((project) => (
                <motion.div
                  key={project.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3 }}
                >
                  <ProjectCard project={project} />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-12 border border-dashed border-white/10 rounded-xl">
              <p className="text-slate-400 font-mono">No projects found matching the filter selection.</p>
            </div>
          )}
        </section>

        {/* EXPERIENCE & TIMELINE SECTION */}
        <section className="space-y-8">
          <div className="text-center md:text-left space-y-1">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-purple-400 font-mono">Career & Awards</h2>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">Experience & Highlights</h3>
          </div>

          <div className="relative border-l border-slate-800 ml-4 md:ml-6 pl-6 md:pl-8 space-y-10 py-2">
            {EXPERIENCE.map((exp, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5, delay: idx * 0.15 }}
                className="relative group"
              >
                {/* Timeline Dot Indicator */}
                <span className="absolute left-0 -ml-[35px] md:-ml-[43px] top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-slate-950 border border-slate-800 text-sm group-hover:border-indigo-500/50 group-hover:shadow-[0_0_10px_rgba(99,102,241,0.2)] transition-all duration-300">
                  {exp.icon}
                </span>

                <div className="space-y-2 p-5 rounded-xl bg-slate-900/30 border border-white/5 hover:border-slate-800/80 hover:bg-slate-900/60 transition-all duration-300 shadow-sm">
                  {/* Header Metas */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                    <h4 className="text-base sm:text-lg font-bold text-white group-hover:text-indigo-400 transition-colors duration-300">
                      {exp.role}
                    </h4>
                    <span className="text-xs font-mono font-medium px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 w-fit">
                      {exp.period}
                    </span>
                  </div>

                  <p className="text-sm font-semibold text-slate-300">{exp.company}</p>
                  
                  <p className="text-sm text-slate-400 leading-relaxed font-normal pt-1">
                    {exp.description}
                  </p>

                  {/* Badges */}
                  <div className="flex flex-wrap gap-1.5 pt-3">
                    {exp.tags.map((tag, tIdx) => (
                      <span
                        key={tIdx}
                        className="text-[10px] font-mono font-semibold px-2 py-0.5 rounded-md bg-slate-800/50 border border-slate-700/30 text-slate-400"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* FOOTER */}
        <footer className="pt-12 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4 text-xs font-mono text-slate-500">
          <p>© {new Date().getFullYear()} {PROFILE.name}. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <span className="hover:text-slate-300 cursor-pointer" onClick={handleCopyEmail}>Email</span>
            <span>•</span>
            <span className="hover:text-slate-300 cursor-pointer" onClick={handleLinkedInClick}>LinkedIn</span>
            <span>•</span>
            <span className="hover:text-slate-300 cursor-pointer" onClick={handleGithubClick}>GitHub</span>
          </div>
        </footer>

      </div>
    </div>
  );
};

export default Home;
